import React, { useCallback, useEffect, useId, useRef, useState } from 'react'
import './OutreachPage.css'

/*
 * Служебный дашборд телеграм-рассылки (services/tg-outreach) — открывается из
 * шапки карты («Рассылка») после входа администратора. Три экрана, один компонент:
 *   /outreach                                      — сводка, загрузка .session, прокси, таблица сессий
 *   /outreach/sessions/<name>                      — диалоги сессии (как в Telegram)
 *   /outreach/sessions/<name>/dialogs/<tg_user_id> — переписка в диалоге
 * Доступ — по логину/паролю админа (POST /api/admin/login): токен сессии лежит в
 * localStorage['realty-map-admin-token'] и уходит заголовком X-Admin-Token во все
 * запросы /api/outreach/*; карта читает тот же ключ и включает свои debug-блоки.
 * Старый вход через ?debug=<секрет> не работает: параметр из адреса убираем.
 * Любой 401 — токен протух: форма входа поверх текущего экрана, опрос стоит,
 * состояние экрана (выбранные файлы, черновики) не теряется.
 * Записи: ручное сообщение из переписки (очередь outreach.outbox), загрузка
 * .session-файлов в TG_SESSIONS_DIR, общий/свой прокси (outreach.settings /
 * tg_accounts.proxy_url) — применяет воркер при следующем захвате/переподключении.
 */

const API_BASE = (
  process.env.REACT_APP_API_BASE ||
  (
    process.env.NODE_ENV === 'production' &&
    typeof window !== 'undefined' &&
    window.location?.origin
  ) ||
  'http://localhost:8001'
).replace(/\/$/, '')

const ADMIN_TOKEN_KEY = 'realty-map-admin-token'

const OVERVIEW_POLL_MS = 5000
const DIALOGS_POLL_MS = 10000
const MESSAGES_POLL_MS = 5000
// Пока в переписке есть ручное сообщение в очереди/отправке — опрашиваем чаще:
// воркер заглядывает в outbox раз в несколько секунд, статус меняется быстро.
const MESSAGES_POLL_FAST_MS = 2000
const DIALOGS_PAGE = 100
const MESSAGES_LIMIT = 300
// Предел Telegram на длину сообщения (бэкенд проверяет то же).
const MESSAGE_MAX_CHARS = 4096
// Лимиты загрузки .session — те же, что проверяет бэкенд (session_upload.py).
const UPLOAD_MAX_FILES = 20
const UPLOAD_MAX_FILE_BYTES = 16 * 1024 * 1024
const UPLOAD_MAX_TOTAL_BYTES = 64 * 1024 * 1024
const PROXY_PLACEHOLDER = 'socks5://user:pass@host:port'

const HEALTH_LABEL = {
  new: 'новая',
  active: 'активна',
  cooldown: 'карантин',
  banned: 'бан',
  unauthorized: 'разлогинена',
}
const ACTIVITY_LABEL = {
  running: 'работает',
  idle: 'простаивает',
  cooldown: 'карантин',
  dead: '—',
}
const SEND_STATUS_LABEL = {
  queued: 'в очереди',
  claimed: 'взята',
  sending: 'отправляется',
  sent: 'отправлено',
  failed: 'ошибка',
  skipped: 'пропущено',
}
// Статусы ручного сообщения из дашборда (outreach.outbox).
const MANUAL_STATUS_LABEL = {
  queued: 'в очереди',
  claimed: 'отправляется…',
  sending: 'отправляется…',
  sent: 'отправлено',
  failed: 'не отправлено',
  cancelled: 'отменено',
}
const MANUAL_PENDING = ['queued', 'claimed', 'sending']
// Итог по файлу в ответе POST /api/outreach/sessions/upload.
const UPLOAD_STATUS_LABEL = { added: 'добавлен', skipped: 'пропущен', rejected: 'отклонён' }
const UPLOAD_STATUS_KIND = { added: 'ok', skipped: 'warn', rejected: 'bad' }

function isPendingManual(m) {
  return m && m.kind === 'manual' && MANUAL_PENDING.includes(m.status)
}

/* ------------------------------------------------------------------ utils */

function loadAdminToken() {
  if (typeof window === 'undefined') return ''
  try {
    return String(window.localStorage.getItem(ADMIN_TOKEN_KEY) || '')
  } catch (_) {
    return ''
  }
}

function saveAdminToken(token) {
  if (typeof window === 'undefined') return
  try {
    if (token) window.localStorage.setItem(ADMIN_TOKEN_KEY, token)
    else window.localStorage.removeItem(ADMIN_TOKEN_KEY)
  } catch (_) { /* storage запрещён — сессия проживёт до перезагрузки вкладки */ }
}

/* Старый вход через ?debug=<секрет> бэкенд больше не принимает; параметр убираем
   из адреса, чтобы секрет карты не оседал в истории и закладках. */
function stripLegacyDebugParam() {
  if (typeof window === 'undefined') return
  try {
    const url = new URL(window.location.href)
    if (!url.searchParams.has('debug')) return
    url.searchParams.delete('debug')
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`)
  } catch (_) { /* replaceState затроттлен — не страшно */ }
}

function parseRoute(pathname) {
  const parts = String(pathname || '').split('/').filter(Boolean)
  if (parts[0] !== 'outreach') return { view: 'overview' }
  if (parts[1] === 'sessions' && parts[2]) {
    const session = decodeURIComponent(parts[2])
    if (parts[3] === 'dialogs' && parts[4]) {
      return { view: 'dialog', session, tgUserId: decodeURIComponent(parts[4]) }
    }
    return { view: 'session', session }
  }
  return { view: 'overview' }
}

function sessionPath(name) {
  return `/outreach/sessions/${encodeURIComponent(name)}`
}

function dialogPath(name, tgUserId) {
  return `${sessionPath(name)}/dialogs/${encodeURIComponent(String(tgUserId))}`
}

/* Единая реакция на 401 от любого запроса: страница ставит обработчик, который
   сбрасывает токен и показывает форму входа поверх текущего экрана. Сам вход
   (POST /api/admin/login) и проверка сессии вызывают readJson с skipAuthHook.
   В ошибку кладём токен, с которым ушёл запрос (err.token): запрос, стартовавший
   со старым токеном, может вернуть 401 уже после повторного входа — такой 401
   страница игнорирует, чтобы не стереть свежий токен. */
let unauthorizedHandler = null
function setUnauthorizedHandler(fn) {
  unauthorizedHandler = fn
}

async function readJson(resp, opts) {
  if (!resp.ok) {
    let detail = ''
    try {
      const body = await resp.json()
      detail = body && body.detail ? String(body.detail) : ''
    } catch (_) { /* тело не JSON */ }
    const err = new Error(detail || `HTTP ${resp.status}`)
    err.status = resp.status
    err.token = opts && opts.token ? opts.token : ''
    if (resp.status === 401 && unauthorizedHandler && !(opts && opts.skipAuthHook)) unauthorizedHandler(err)
    throw err
  }
  return resp.json()
}

function authHeaders(token) {
  return token ? { 'X-Admin-Token': token } : {}
}

async function apiGet(path, token, opts) {
  return readJson(await fetch(`${API_BASE}${path}`, { headers: authHeaders(token) }), { ...opts, token })
}

async function apiPost(path, token, body, opts) {
  const headers = { 'Content-Type': 'application/json', ...authHeaders(token) }
  return readJson(await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  }), { ...opts, token })
}

/* multipart/form-data: Content-Type не ставим — браузер сам добавит boundary. */
async function apiUpload(path, token, formData) {
  return readJson(await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: authHeaders(token),
    body: formData,
  }), { token })
}

function errDetail(err) {
  const msg = err && err.message ? String(err.message) : ''
  return /^HTTP \d+$/.test(msg) ? '' : msg
}

function describeError(err) {
  if (!err) return ''
  const detail = errDetail(err)
  switch (err.status) {
    case 400: return detail || 'Некорректный запрос'
    case 401: return 'Сессия администратора истекла — войдите снова'
    case 403: return 'Нет доступа'
    case 404: return detail || 'Не найдено'
    case 409: return detail || 'Конфликт: состояние изменилось — обновите страницу'
    case 411: return 'Запрос ушёл без Content-Length — браузер не передал размер тела'
    case 413: return `Слишком большой запрос: суммарный размер файлов больше ${fmtBytes(UPLOAD_MAX_TOTAL_BYTES)}`
    case 503: return `Недоступно: ${detail || 'сервис не готов'}`
    case 507: return `На сервере не хватает места${detail ? `: ${detail}` : ''}`
    default: break
  }
  if (!err.status) return `Бэкенд недоступен${detail ? ` (${detail})` : ''}`
  return detail || `HTTP ${err.status}`
}

/* Тексты ошибок формы входа (POST /api/admin/login). */
function loginErrorText(err) {
  if (!err) return ''
  if (err.status === 401) return 'Неверный логин или пароль'
  if (err.status === 503 || err.status === 400) return errDetail(err) || describeError(err)
  if (!err.status) return 'Бэкенд недоступен'
  return describeError(err)
}

function parseTs(value) {
  if (!value) return null
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d
}

function pad2(n) {
  return String(n).padStart(2, '0')
}

function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

const MONTHS_SHORT = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек']

function fmtClock(value) {
  const d = parseTs(value)
  return d ? `${pad2(d.getHours())}:${pad2(d.getMinutes())}` : ''
}

function fmtDay(value) {
  const d = parseTs(value)
  if (!d) return ''
  const now = new Date()
  if (sameDay(d, now)) return 'Сегодня'
  const yesterday = new Date(now.getTime() - 86400000)
  if (sameDay(d, yesterday)) return 'Вчера'
  const year = d.getFullYear() !== now.getFullYear() ? ` ${d.getFullYear()}` : ''
  return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}${year}`
}

/* Как в списке чатов Telegram: сегодня — время, иначе дата. */
function fmtListTime(value) {
  const d = parseTs(value)
  if (!d) return ''
  return sameDay(d, new Date()) ? fmtClock(d) : `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`
}

function fmtDateTime(value) {
  const d = parseTs(value)
  if (!d) return '—'
  return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)} ${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`
}

function fmtAgo(value, now = Date.now()) {
  const d = parseTs(value)
  if (!d) return '—'
  const sec = Math.max(0, Math.round((now - d.getTime()) / 1000))
  if (sec < 60) return `${sec} с назад`
  const min = Math.round(sec / 60)
  if (min < 60) return `${min} мин назад`
  const hours = Math.round(min / 60)
  if (hours < 48) return `${hours} ч назад`
  return `${Math.round(hours / 24)} дн назад`
}

function fmtBytes(n) {
  const v = Number(n) || 0
  if (v < 1024) return `${v} Б`
  if (v < 1024 * 1024) return `${Math.round(v / 1024)} КиБ`
  const mb = v / (1024 * 1024)
  return `${Number.isInteger(mb) ? mb : mb.toFixed(1)} МиБ`
}

function plural(n, one, few, many) {
  const a = Math.abs(Number(n) || 0) % 100
  const b = a % 10
  if (a > 10 && a < 20) return many
  if (b > 1 && b < 5) return few
  if (b === 1) return one
  return many
}

function fmtPhone(digits) {
  const s = String(digits || '').replace(/\D/g, '')
  if (!s) return ''
  if (s.length === 12 && s.startsWith('998')) {
    return `+998 ${s.slice(3, 5)} ${s.slice(5, 8)} ${s.slice(8, 10)} ${s.slice(10, 12)}`
  }
  return `+${s}`
}

function peerTitle(peer) {
  if (!peer) return ''
  if (peer.tg_username) return `@${peer.tg_username}`
  if (peer.phone) return fmtPhone(peer.phone)
  return peer.tg_user_id ? `id ${peer.tg_user_id}` : '—'
}

function peerSubtitle(peer) {
  if (!peer) return ''
  const parts = []
  if (peer.tg_username && peer.phone) parts.push(fmtPhone(peer.phone))
  if (peer.tg_user_id) parts.push(`id ${peer.tg_user_id}`)
  return parts.join(' · ')
}

function initials(text) {
  const clean = String(text || '').replace(/^[@+]/, '').trim()
  if (!clean) return '?'
  const words = clean.split(/\s+/).filter(Boolean)
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase()
  return clean.slice(0, 2).toUpperCase()
}

/* Детерминированный цвет аватарки по id — как в Telegram. */
const AVATAR_COLORS = ['#e17076', '#eda86c', '#a695e7', '#7bc862', '#6ec9cb', '#65aadd', '#ee7aae']
function avatarColor(seed) {
  const s = String(seed || '')
  let h = 0
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0
  return AVATAR_COLORS[h % AVATAR_COLORS.length]
}

/* Ссылка на карту с объявлением — те же параметры, что кладёт в сообщение
   links.build_url (ad_id + фильтры типа сделки/объекта). Секрет в URL не нужен:
   карта берёт админский токен из localStorage сама. */
function mapHref(ad) {
  const params = new URLSearchParams()
  if (ad && ad.ad_id) params.set('ad_id', String(ad.ad_id))
  if (ad && ad.deal_type) params.set('parsed_deal_type', ad.deal_type)
  if (ad && ad.object_type) params.set('parsed_object_type', ad.object_type)
  const qs = params.toString()
  return qs ? `/?${qs}` : '/'
}

function accountTitle(session) {
  if (!session) return ''
  if (session.tg_username) return `@${session.tg_username}`
  if (session.display_name) return session.display_name
  if (session.phone) return fmtPhone(session.phone)
  return session.session_name
}

function proxyLabel(proxy) {
  if (!proxy) return '—'
  if (proxy.in_use) return proxy.in_use
  if (proxy.source === 'direct') return 'напрямую'
  return '—'
}

/* Откуда взялся прокси, который воркер реально поднял (source); для общего —
   из настроек дашборда (outreach.settings) или из TG_PROXY_URL воркера. */
function proxyHint(proxy, shared) {
  if (!proxy) return ''
  if (proxy.source === 'account') return 'свой прокси аккаунта'
  if (proxy.source === 'shared') {
    if (!shared) return 'общий прокси'
    return shared.mode === 'url' ? 'общий из настроек дашборда' : 'общий из TG_PROXY_URL'
  }
  if (proxy.source === 'direct') return 'напрямую'
  return 'сессия ещё не стартовала с воркером, который записывает прокси'
}

/* Свой режим аккаунта (tg_accounts.proxy_url) в виде, который понимает ProxyEditor. */
function ownProxy(proxy) {
  const p = proxy || {}
  return { mode: p.mode || 'inherit', value: p.own || null, has_credentials: Boolean(p.own_has_credentials) }
}

function ownProxyText(proxy) {
  const p = proxy || {}
  if (p.mode === 'direct') return 'напрямую'
  if (p.mode === 'url') return p.own || '?'
  return 'общий'
}

/* Что должно быть (expected) не совпадает с тем, что поднято (in_use): новый
   прокси применится, когда воркер переподключится или заново захватит сессию. */
function proxyNeedsReclaim(proxy) {
  if (!proxy || proxy.expected === undefined || proxy.expected === '?') return false
  return (proxy.expected || null) !== (proxy.in_use || null)
}

function hasCredentials(url) {
  return /^[a-z0-9+.-]+:\/\/[^/@]+@/i.test(String(url || ''))
}

function sessionRunning(s) {
  if (!s) return false
  if (s.running != null) return Boolean(s.running)
  return s.activity === 'running'
}

/* ------------------------------------------------------------------ hooks */

/* Опрос с паузой на скрытой вкладке; ошибка не затирает последние данные.
   enabled=false (нет админского токена) — опрос стоит, данные остаются. */
function usePolling(fetcher, intervalMs, deps, enabled = true) {
  const [state, setState] = useState({ data: null, error: null, loading: true, updatedAt: null })
  const [tick, setTick] = useState(0)
  const fetcherRef = useRef(fetcher)
  fetcherRef.current = fetcher
  // deps — скаляры (токен, имя сессии, запрос); склеиваем в ключ, чтобы эффект
  // перезапускался ровно при их смене (и чтобы линтер видел статичный массив).
  const depsKey = deps.map(d => String(d)).join('\u001f')

  useEffect(() => {
    if (!enabled) return undefined
    let cancelled = false
    let timer = null

    const schedule = () => {
      if (cancelled) return
      timer = setTimeout(load, intervalMs)
    }
    const load = async () => {
      if (cancelled) return
      if (typeof document !== 'undefined' && document.hidden) {
        schedule()
        return
      }
      try {
        const data = await fetcherRef.current()
        if (!cancelled) setState({ data, error: null, loading: false, updatedAt: Date.now() })
      } catch (e) {
        if (!cancelled) setState(prev => ({ ...prev, error: e, loading: false }))
      }
      schedule()
    }
    const onVisible = () => {
      if (typeof document !== 'undefined' && !document.hidden) {
        clearTimeout(timer)
        load()
      }
    }

    setState(prev => ({ ...prev, loading: true }))
    load()
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      cancelled = true
      clearTimeout(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [depsKey, tick, intervalMs, enabled])

  const refresh = useCallback(() => setTick(t => t + 1), [])
  return { ...state, refresh }
}

/* Тикает раз в секунду — для «обновлено N с назад». */
function useNow(stepMs = 1000) {
  const [now, setNow] = useState(Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), stepMs)
    return () => clearInterval(id)
  }, [stepMs])
  return now
}

function useDebounced(value, delay) {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(id)
  }, [value, delay])
  return debounced
}

/* ------------------------------------------------------------------ atoms */

function Badge({ kind, children, title }) {
  return <span className={`or-badge or-badge-${kind || 'neutral'}`} title={title}>{children}</span>
}

function HealthBadge({ status }) {
  const kind = status === 'active' ? 'ok'
    : status === 'cooldown' ? 'warn'
      : (status === 'banned' || status === 'unauthorized') ? 'bad'
        : 'neutral'
  return <Badge kind={kind}>{HEALTH_LABEL[status] || status || '—'}</Badge>
}

function ActivityDot({ activity }) {
  return (
    <span className={`or-activity or-activity-${activity || 'idle'}`}>
      <span className="or-activity-dot" aria-hidden />
      {ACTIVITY_LABEL[activity] || activity}
    </span>
  )
}

function SendStatusBadge({ status }) {
  const kind = status === 'sent' ? 'ok'
    : status === 'failed' ? 'bad'
      : status === 'skipped' ? 'neutral'
        : (status === 'claimed' || status === 'sending') ? 'live'
          : 'queue'
  return <Badge kind={kind}>{SEND_STATUS_LABEL[status] || status}</Badge>
}

function Avatar({ seed, text, small }) {
  return (
    <span
      className={`or-avatar${small ? ' or-avatar-small' : ''}`}
      style={{ background: avatarColor(seed) }}
      aria-hidden
    >
      {initials(text)}
    </span>
  )
}

function AdLink({ ad, className }) {
  if (!ad) return null
  const title = ad.title || ad.ad_id || 'объявление'
  return (
    <a
      className={className || 'or-ad-link'}
      href={mapHref(ad)}
      target="_blank"
      rel="noopener noreferrer"
      title={`Открыть на карте${ad.city ? ` · ${ad.city}` : ''}`}
    >
      {title}
    </a>
  )
}

function StatusLine({ updatedAt, loading, error, onRefresh, now }) {
  return (
    <div className={`or-statusline${error ? ' or-statusline-error' : ''}`} role="status" aria-live="polite">
      {error ? (
        <span className="or-statusline-text">{describeError(error)}</span>
      ) : (
        <span className="or-statusline-text">
          {updatedAt ? `обновлено ${fmtAgo(new Date(updatedAt).toISOString(), now)}` : 'загрузка…'}
          {loading && updatedAt ? ' · обновляется' : ''}
        </span>
      )}
      <button type="button" className="or-btn or-btn-small" onClick={onRefresh}>Обновить</button>
    </div>
  )
}

/* ------------------------------------------------------------------ admin gate */

/* Форма входа администратора. overlay — токен протух посреди работы: форма
   поверх текущего экрана, его состояние не трогаем. hint — подсказка бэкенда
   из 401 («admin login is not configured …»). */
function AdminGate({ overlay, hint, onLogin }) {
  const [login, setLogin] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const loginId = useId()
  const passwordId = useId()

  const submit = async e => {
    e.preventDefault()
    if (busy) return
    setBusy(true)
    setError('')
    try {
      const resp = await apiPost('/api/admin/login', '', { login: login.trim(), password }, { skipAuthHook: true })
      if (!resp || !resp.token) throw new Error('бэкенд не вернул токен сессии')
      setPassword('')
      onLogin(resp)
    } catch (err) {
      setError(loginErrorText(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={`or-gate${overlay ? ' or-gate-in-overlay' : ''}`}>
      <form className="or-gate-card" onSubmit={submit}>
        <h2 className="or-gate-title">Вход администратора</h2>
        <p className="or-muted or-gate-text">
          {overlay
            ? 'Сессия администратора истекла — войдите снова, текущий экран сохранится.'
            : 'Дашборд рассылки доступен только администратору: логин и пароль задаются на сервере (ADMIN_LOGIN / ADMIN_PASSWORD).'}
        </p>
        {hint ? <div className="or-form-hint">{hint}</div> : null}
        <div className="or-field">
          <label className="or-label" htmlFor={loginId}>Логин</label>
          <input
            id={loginId}
            className="or-input"
            type="text"
            name="login"
            autoComplete="username"
            value={login}
            onChange={e => setLogin(e.target.value)}
            disabled={busy}
            aria-label="Логин администратора"
            required
          />
        </div>
        <div className="or-field">
          <label className="or-label" htmlFor={passwordId}>Пароль</label>
          <input
            id={passwordId}
            className="or-input"
            type="password"
            name="password"
            autoComplete="current-password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            disabled={busy}
            aria-label="Пароль администратора"
            required
          />
        </div>
        {error ? <div className="or-form-error" role="alert">{error}</div> : null}
        <div className="or-gate-actions">
          <button type="submit" className="or-btn or-btn-primary" disabled={busy || !login.trim() || !password}>
            {busy ? 'проверяю…' : 'Войти'}
          </button>
          {!overlay ? <a className="or-link" href="/">← на карту</a> : null}
        </div>
        <p className="or-muted or-gate-note">Неудачные попытки бэкенд намеренно замедляет (до 8 с) — не нажимайте повторно.</p>
      </form>
    </div>
  )
}

/* ------------------------------------------------------------------ proxy */

/* Общий редактор прокси — для настроек дашборда (scope=shared) и для аккаунта
   (scope=session). Поле всегда пустое: креды в браузер не уходят, placeholder —
   текущая маска. onSave(payload) — async; ошибка показывается под полем. */
function ProxyEditor({ current, scope, placeholder, onSave, onCancel }) {
  const cur = current || {}
  const [mode, setMode] = useState(cur.mode || 'inherit')
  const [url, setUrl] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const groupName = useId()
  const shared = scope === 'shared'
  const inheritLabel = shared ? 'Из конфига воркера (TG_PROXY_URL)' : 'Общий (настройки дашборда или TG_PROXY_URL)'

  const submit = async e => {
    e.preventDefault()
    if (busy) return
    const value = url.trim()
    if (mode === 'url') {
      if (!value) {
        setError('укажите URL прокси')
        return
      }
      if (/^https:\/\//i.test(value)) {
        setError('https:// не поддерживается — используйте socks5://, socks5h://, socks4:// или http://')
        return
      }
      if (cur.has_credentials && !hasCredentials(value)) {
        const ok = window.confirm('Текущий прокси — с логином и паролем, а новый URL без них. Сохранить без авторизации?')
        if (!ok) return
      }
    }
    if (mode === 'direct' && cur.mode !== 'direct') {
      const ok = window.confirm(shared
        ? 'Все аккаунты пойдут с IP ноды — сильный сигнал для антиспама Telegram. Продолжить?'
        : 'Аккаунт пойдёт с IP ноды — сильный сигнал для антиспама Telegram. Продолжить?')
      if (!ok) return
    }
    setBusy(true)
    setError('')
    try {
      await onSave(mode === 'url' ? { mode, proxy_url: value } : { mode })
    } catch (err) {
      setError(describeError(err))
    } finally {
      setBusy(false)
    }
  }

  const radio = (value, label) => (
    <label className="or-radio">
      <input
        type="radio"
        name={groupName}
        value={value}
        checked={mode === value}
        onChange={() => { setMode(value); setError('') }}
        disabled={busy}
      />
      {' '}{label}
    </label>
  )

  return (
    <form className="or-proxy-editor" onSubmit={submit} onClick={e => e.stopPropagation()} aria-label="Настройка прокси">
      {radio('inherit', inheritLabel)}
      {radio('direct', 'Напрямую')}
      {radio('url', 'Свой URL')}
      {mode === 'url' ? (
        <>
          <input
            className="or-input or-mono or-proxy-input"
            type="text"
            value={url}
            onChange={e => { setUrl(e.target.value); setError('') }}
            placeholder={placeholder || PROXY_PLACEHOLDER}
            aria-label="URL прокси"
            autoComplete="off"
            spellCheck={false}
            disabled={busy}
          />
          <div className="or-proxy-caption">полный URL, включая логин:пароль@host:port; https:// не поддерживается</div>
        </>
      ) : null}
      {error ? <div className="or-field-error" role="alert">{error}</div> : null}
      <div className="or-proxy-actions">
        <button type="submit" className="or-btn or-btn-small or-btn-primary" disabled={busy}>{busy ? 'сохраняю…' : 'Сохранить'}</button>
        <button type="button" className="or-btn or-btn-small" onClick={onCancel} disabled={busy}>Отмена</button>
      </div>
    </form>
  )
}

function envProxyText(env) {
  const e = env || {}
  if (e.known === false) return 'неизвестно бэкенду'
  if (e.direct || !e.value) return 'напрямую'
  return e.value
}

function sharedProxyText(p) {
  if (p.mode === 'direct') return 'напрямую (задано со страницы)'
  if (p.mode === 'url') return `${p.value || '?'} (${p.has_credentials ? 'с авторизацией' : 'без авторизации'})`
  return `из конфига воркера (TG_PROXY_URL): ${envProxyText(p.env)}`
}

/* Карточка «Общий прокси» — outreach.settings, действует для сессий без своего. */
function ProxyCard({ token, proxy, onSaved }) {
  const [editing, setEditing] = useState(false)
  const [notice, setNotice] = useState('')
  const [unavailable, setUnavailable] = useState(false)
  const p = proxy || {}
  const available = p.available !== false
  // Таблица появилась (обновили tg-outreach) — снимаем read-only после 503.
  useEffect(() => {
    if (available) setUnavailable(false)
  }, [available])
  const readOnly = !available || unavailable

  const save = async payload => {
    let resp
    try {
      resp = await apiPost('/api/outreach/settings/proxy', token, payload)
    } catch (err) {
      if (err.status === 503) {
        setUnavailable(true)
        setEditing(false)
        return
      }
      throw err
    }
    const n = Number(resp && resp.affected_running) || 0
    setNotice(n
      ? `${n} ${plural(n, 'работающая сессия переподключится', 'работающие сессии переподключатся', 'работающих сессий переподключатся')} в течение ~20 с`
      : 'Сохранено. Работающих сессий без своего прокси сейчас нет — применится при следующем захвате.')
    setEditing(false)
    onSaved()
  }

  return (
    <section className="or-card" aria-label="Общий прокси">
      <h3 className="or-card-title">Общий прокси <span className="or-h2-hint">для сессий без своего</span></h3>
      {editing && !readOnly ? (
        <ProxyEditor
          current={p}
          scope="shared"
          placeholder={p.mode === 'url' ? p.value : ((p.env && p.env.value) || PROXY_PLACEHOLDER)}
          onSave={save}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <div className="or-card-body">
          <div className="or-proxy-current">
            <span className="or-mono">{sharedProxyText(p)}</span>
            {readOnly ? null : (
              <button type="button" className="or-btn or-btn-small" onClick={() => { setNotice(''); setEditing(true) }}>Изменить</button>
            )}
          </div>
          {p.updated_ts ? (
            <div className="or-muted">изменено {fmtDateTime(p.updated_ts)}{p.updated_by ? ` · ${p.updated_by}` : ''}</div>
          ) : null}
          {readOnly ? (
            <div className="or-muted">обновите tg-outreach: таблица outreach.settings появится после outreach.sh status или старта воркера</div>
          ) : null}
          {notice ? <div className="or-notice">{notice}</div> : null}
        </div>
      )}
    </section>
  )
}

/* Статус прокси сессии рядом с тем, что реально поднято: воркер ещё не
   переподключился (pending) или сессия не запущена и новый прокси ждёт захвата. */
function ProxyStatus({ proxy, running, notice }) {
  const p = proxy || {}
  const waiting = proxyNeedsReclaim(p)
  return (
    <>
      {p.pending === true || waiting ? <div className="or-muted">задано: {ownProxyText(p)}</div> : null}
      {p.pending === true ? (
        <div><Badge kind="warn" title="воркер ещё не применил новый прокси">переподключается…</Badge></div>
      ) : null}
      {!running && waiting ? <div className="or-muted">применится при следующем захвате</div> : null}
      {notice ? <div className="or-notice">{notice}</div> : null}
    </>
  )
}

/* ------------------------------------------------------------------ upload */

function UploadResults({ result, href, navigate }) {
  const rows = (result && result.results) || []
  const workers = (result && result.workers) || {}
  const warnings = (result && result.warnings) || []
  return (
    <div className="or-results">
      <div className="or-results-summary">
        добавлено {result.added || 0} · пропущено {result.skipped || 0} · отклонено {result.rejected || 0}
      </div>
      <div className="or-table-wrap">
        <table className="or-table or-table-results">
          <thead>
            <tr><th>Файл</th><th>Итог</th><th>Причина</th></tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const linkable = r.conflict_with && r.code !== 'name_retired' && !(r.existing && r.existing.retired)
              return (
                <tr key={`${r.index}-${r.filename}`}>
                  <td className="or-mono or-results-file">
                    {r.filename}
                    {r.size ? <span className="or-muted"> · {fmtBytes(r.size)}</span> : null}
                  </td>
                  <td><Badge kind={UPLOAD_STATUS_KIND[r.status] || 'neutral'}>{UPLOAD_STATUS_LABEL[r.status] || r.status}</Badge></td>
                  <td className="or-results-reason">
                    {r.reason || (r.status === 'added' ? 'файл записан в директорию сессий' : '')}
                    {r.conflict_with ? (
                      <>
                        {' · '}
                        {linkable ? (
                          <a className="or-link or-mono" href={href(sessionPath(r.conflict_with))} onClick={e => { e.preventDefault(); navigate(sessionPath(r.conflict_with)) }}>{r.conflict_with}</a>
                        ) : <span className="or-mono">{r.conflict_with}</span>}
                        {r.existing ? (
                          <span className="or-muted">
                            {r.existing.retired ? ' (в retired/)' : ` (${HEALTH_LABEL[r.existing.status] || r.existing.status || '?'}${r.existing.has_file === false ? ', файла нет' : ''})`}
                          </span>
                        ) : null}
                      </>
                    ) : null}
                    {r.status === 'added' && (r.dc_id || r.server) ? (
                      <span className="or-muted"> · DC{r.dc_id == null ? '?' : r.dc_id}{r.server ? ` ${r.server}` : ''}</span>
                    ) : null}
                    {r.status === 'added' && r.registered === false ? (
                      <span className="or-muted"> · строка в БД появится при старте воркера</span>
                    ) : null}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      {warnings.map(w => <div key={w} className="or-warnline">{w}</div>)}
      {result.added > 0 ? (
        <div className="or-hint">
          Файлы подхватит свободный воркер в течение ~минуты. Сейчас работает {workers.running == null ? '—' : workers.running} из {workers.files == null ? '—' : workers.files} файлов —
          если свободных реплик нет, выполните на сервере: <span className="or-mono">sudo bash /opt/olxuz/infra/k8s/outreach.sh start</span>
        </div>
      ) : null}
    </div>
  )
}

/* 400/409 от загрузки бэкенд отдаёт короткими английскими detail — переводим. */
function uploadErrorText(err) {
  if (err && err.status === 409) return 'Другая загрузка ещё идёт — подождите и повторите'
  if (err && err.status === 400) {
    const detail = errDetail(err)
    if (/^no files/i.test(detail)) return 'Файлы не выбраны'
    if (/^too many files/i.test(detail)) return `Не больше ${UPLOAD_MAX_FILES} файлов за раз`
  }
  return describeError(err)
}

/* Карточка «Добавить сессии»: выбор/перетаскивание .session-файлов, проверка
   лимитов на клиенте, POST multipart, таблица итогов по каждому файлу. */
function SessionUpload({ token, sessionsDir, href, navigate, onUploaded }) {
  const [files, setFiles] = useState([])
  const [skipped, setSkipped] = useState([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState(null)
  const [dragOver, setDragOver] = useState(false)
  const inputRef = useRef(null)
  const inputId = useId()
  const dir = sessionsDir || {}
  const unconfigured = dir.configured === false
  const disabled = unconfigured || busy

  const addFiles = list => {
    const incoming = Array.from(list || [])
    if (!incoming.length) return
    const next = [...files]
    const bad = []
    incoming.forEach(f => {
      if (!/\.session$/i.test(f.name)) {
        bad.push(f.name)
        return
      }
      if (next.some(x => x.name === f.name)) return // тот же файл дважды — берём первый
      next.push(f)
    })
    setFiles(next)
    setSkipped(prev => Array.from(new Set([...prev, ...bad])))
    setResult(null)
    setError('')
  }
  const removeFile = name => setFiles(prev => prev.filter(f => f.name !== name))
  const clear = () => {
    setFiles([])
    setSkipped([])
    setError('')
    if (inputRef.current) inputRef.current.value = ''
  }

  const total = files.reduce((sum, f) => sum + f.size, 0)
  const oversize = files.filter(f => f.size > UPLOAD_MAX_FILE_BYTES)
  const problems = []
  if (files.length > UPLOAD_MAX_FILES) problems.push(`не больше ${UPLOAD_MAX_FILES} файлов за раз (выбрано ${files.length})`)
  if (oversize.length) problems.push(`больше ${fmtBytes(UPLOAD_MAX_FILE_BYTES)}: ${oversize.map(f => f.name).join(', ')}`)
  if (total > UPLOAD_MAX_TOTAL_BYTES) problems.push(`суммарно больше ${fmtBytes(UPLOAD_MAX_TOTAL_BYTES)} (${fmtBytes(total)})`)
  const canUpload = !disabled && files.length > 0 && !problems.length

  const upload = async () => {
    if (!canUpload) return
    setBusy(true)
    setError('')
    try {
      const fd = new FormData()
      files.forEach(f => fd.append('files', f, f.name))
      const resp = await apiUpload('/api/outreach/sessions/upload', token, fd)
      setResult(resp)
      setFiles([])
      setSkipped([])
      if (inputRef.current) inputRef.current.value = ''
      onUploaded()
    } catch (err) {
      setError(uploadErrorText(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="or-card" aria-label="Добавить сессии">
      <h3 className="or-card-title">Добавить сессии <span className="or-h2-hint">.session-файлы Telethon → TG_SESSIONS_DIR</span></h3>
      {unconfigured ? (
        <div className="or-field-error">
          загрузка выключена: TG_SESSIONS_DIR не примонтирован в realty-map-backend{dir.problem ? ` · ${dir.problem}` : ''}
        </div>
      ) : null}
      <div
        className={`or-drop${dragOver ? ' or-drop-over' : ''}${disabled ? ' or-drop-disabled' : ''}`}
        onDragOver={e => { e.preventDefault(); if (!disabled) setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); if (!disabled) addFiles(e.dataTransfer.files) }}
      >
        <input
          ref={inputRef}
          id={inputId}
          className="or-file-input"
          type="file"
          multiple
          accept=".session"
          disabled={disabled}
          onChange={e => { addFiles(e.target.files); e.target.value = '' }}
          aria-label="Файлы сессий Telegram (.session)"
        />
        <label htmlFor={inputId} className="or-drop-label">
          Перетащите .session-файлы сюда или <span className="or-link">выберите на диске</span>
        </label>
        <div className="or-upload-limits">до {UPLOAD_MAX_FILES} файлов, каждый ≤ {fmtBytes(UPLOAD_MAX_FILE_BYTES)}, всего ≤ {fmtBytes(UPLOAD_MAX_TOTAL_BYTES)}</div>
      </div>
      {files.length ? (
        <ul className="or-file-list">
          {files.map(f => {
            const big = f.size > UPLOAD_MAX_FILE_BYTES
            return (
              <li key={f.name} className={`or-file-item${big ? ' or-file-bad' : ''}`}>
                <span className="or-mono or-file-name">{f.name}</span>
                <span className="or-file-size">{fmtBytes(f.size)}{big ? ' — больше лимита' : ''}</span>
                <button type="button" className="or-btn-link" onClick={() => removeFile(f.name)} disabled={busy} aria-label={`Убрать ${f.name}`}>убрать</button>
              </li>
            )
          })}
        </ul>
      ) : null}
      {skipped.length ? (
        <div className="or-muted or-file-skipped">
          {skipped.map(n => <div key={n}><span className="or-mono">{n}</span> — не .session — пропущен</div>)}
        </div>
      ) : null}
      {problems.map(p => <div key={p} className="or-field-error">{p}</div>)}
      <div className="or-upload-actions">
        <button type="button" className="or-btn or-btn-primary" disabled={!canUpload} onClick={upload}>
          {busy ? 'проверяю и записываю…' : `Загрузить${files.length ? ` (${files.length}, ${fmtBytes(total)})` : ''}`}
        </button>
        {(files.length || skipped.length) && !busy ? (
          <button type="button" className="or-btn" onClick={clear}>Очистить</button>
        ) : null}
      </div>
      {error ? <div className="or-field-error" role="alert">{error}</div> : null}
      {result ? <UploadResults result={result} href={href} navigate={navigate} /> : null}
      {dir.path ? (
        <div className="or-muted or-card-dir">
          директория: <span className="or-mono">{dir.path}</span>
          {dir.writable === false ? ' · только чтение' : ''}
          {dir.stale_tmp ? ` · остатков .upload-*.tmp: ${dir.stale_tmp}` : ''}
        </div>
      ) : null}
      <p className="or-card-note">
        Один аккаунт Telegram = один файл: копию уже загруженной сессии бэкенд отклонит, а второй файл того же аккаунта
        с другим auth_key воркер отключит при первом старте. Сессии должны быть созданы с теми же TELEGRAM_API_ID/HASH, что у воркера.
      </p>
    </section>
  )
}

/* ------------------------------------------------------------------ overview */

function StatTile({ label, value, hint, accent }) {
  return (
    <div className={`or-tile${accent ? ` or-tile-${accent}` : ''}`} title={hint}>
      <div className="or-tile-value">{value == null ? '—' : value}</div>
      <div className="or-tile-label">{label}</div>
    </div>
  )
}

function Overview({ token, href, navigate, now }) {
  const { data, error, loading, updatedAt, refresh } = usePolling(
    () => apiGet('/api/outreach/overview', token),
    OVERVIEW_POLL_MS,
    [token],
    Boolean(token)
  )
  const stats = (data && data.stats) || {}
  const sessions = (data && data.sessions) || []
  const campaigns = (data && data.campaigns) || []
  const recent = (data && data.recent) || []
  const schemaMissing = Boolean(data) && data.schema_ready === false
  const sessionsDir = (data && data.sessions_dir) || null
  const sharedProxy = (data && data.settings && data.settings.proxy) || null
  const unregistered = (sessionsDir && sessionsDir.unregistered) || []
  const retired = (sessionsDir && sessionsDir.retired) || []

  return (
    <div className="or-overview">
      <StatusLine updatedAt={updatedAt} loading={loading} error={error} onRefresh={refresh} now={now} />

      {schemaMissing ? (
        <div className="or-banner" role="alert">
          tg-outreach ещё не запускался: схемы outreach.* в БД нет. Загрузите .session файлы и выполните outreach.sh start
          {data.schema_problem ? <span className="or-muted"> · {data.schema_problem}</span> : null}
        </div>
      ) : null}

      {!schemaMissing ? (
        <section className="or-tiles" aria-label="Сводка">
          <StatTile label="отправлено за час" value={stats.sent_last_hour} accent="ok" />
          <StatTile label="отправлено за 24 ч" value={stats.sent_last_day} accent="ok" />
          <StatTile label="отправлено всего" value={stats.sent_total} />
          <StatTile label="в очереди" value={stats.queued} hint="status = queued" />
          <StatTile label="в работе сейчас" value={stats.in_progress} hint="claimed + sending" accent="live" />
          <StatTile label="ошибок" value={stats.failed} accent={stats.failed ? 'bad' : undefined} />
          <StatTile label="пропущено" value={stats.skipped} hint="нет Telegram, приватность, отписка" />
          <StatTile label="ответов за 24 ч" value={stats.replies_last_day} hint={`всего ${stats.replies_total == null ? '—' : stats.replies_total}`} />
          <StatTile label="отписались" value={stats.do_not_contact} hint={`стоп-ответов всего ${stats.stop_replies == null ? '—' : stats.stop_replies}`} accent={stats.do_not_contact ? 'warn' : undefined} />
          <StatTile
            label="сессий работает"
            value={data ? `${data.running_count} / ${data.sessions_total}` : null}
            hint="держат advisory-лок прямо сейчас / всего зарегистрировано"
            accent="live"
          />
          {data && data.outbox_enabled ? (
            <StatTile
              label="ручных в очереди"
              value={stats.outbox_pending}
              hint={`сообщения из дашборда, ждущие воркера сессии · за 24 ч ушло ${stats.outbox_sent_last_day == null ? '—' : stats.outbox_sent_last_day}`}
              accent={stats.outbox_pending ? 'warn' : undefined}
            />
          ) : null}
        </section>
      ) : null}

      {campaigns.length ? (
        <section className="or-section">
          <h2 className="or-h2">Кампании</h2>
          <div className="or-table-wrap">
            <table className="or-table">
              <thead>
                <tr>
                  <th>Кампания</th><th>Статус</th><th>Всего</th>
                  <th>В очереди</th><th>В работе</th><th>Отправлено</th><th>Ошибок</th><th>Пропущено</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map(c => {
                  const n = c.counts || {}
                  return (
                    <tr key={c.code}>
                      <td className="or-mono">{c.code}</td>
                      <td><Badge kind={c.status === 'active' ? 'ok' : 'warn'}>{c.status}</Badge></td>
                      <td>{c.total}</td>
                      <td>{n.queued || 0}</td>
                      <td>{(n.claimed || 0) + (n.sending || 0)}</td>
                      <td>{n.sent || 0}</td>
                      <td>{n.failed || 0}</td>
                      <td>{n.skipped || 0}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {data ? (
        <section className="or-section or-cards" aria-label="Сессии и прокси">
          <SessionUpload token={token} sessionsDir={sessionsDir} href={href} navigate={navigate} onUploaded={refresh} />
          <ProxyCard token={token} proxy={sharedProxy} onSaved={refresh} />
        </section>
      ) : null}

      <section className="or-section">
        <h2 className="or-h2">
          Сессии
          <span className="or-h2-hint">обновляется каждые {OVERVIEW_POLL_MS / 1000} с · клик по строке — диалоги сессии · ✎ — свой прокси</span>
        </h2>
        <div className="or-table-wrap">
          <table className="or-table or-table-sessions">
            <thead>
              <tr>
                <th>Сессия</th>
                <th>Статус</th>
                <th>Активность</th>
                <th>Прокси</th>
                <th>Сегодня</th>
                <th>Час / 24 ч</th>
                <th>Сейчас в работе</th>
                <th>Последняя отправка</th>
                <th>Ответов 24 ч</th>
                <th>Пульс</th>
              </tr>
            </thead>
            <tbody>
              {!sessions.length && !unregistered.length ? (
                <tr><td colSpan={10} className="or-empty">{data ? 'ни одной сессии не зарегистрировано' : (error ? '' : 'загрузка…')}</td></tr>
              ) : sessions.map(s => (
                <SessionRow
                  key={s.session_name}
                  session={s}
                  token={token}
                  href={href}
                  navigate={navigate}
                  now={now}
                  sharedProxy={sharedProxy}
                  onChanged={refresh}
                />
              ))}
              {unregistered.map(name => (
                <tr key={`unregistered-${name}`} className="or-row-unregistered">
                  <td>
                    <span className="or-session-link">
                      <Avatar seed={name} text={name} small />
                      <span className="or-session-name">
                        <span className="or-mono">{name}</span>
                        <span className="or-muted">файл есть, в БД ещё не зарегистрирован — заведёт воркер при старте</span>
                      </span>
                    </span>
                  </td>
                  <td colSpan={9} className="or-muted" />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {retired.length ? (
          <details className="or-retired">
            <summary>retired/ ({retired.length})</summary>
            <ul className="or-retired-list">
              {retired.map(name => <li key={name} className="or-mono">{name}</li>)}
            </ul>
          </details>
        ) : null}
      </section>

      <section className="or-section">
        <h2 className="or-h2">Последние исходы<span className="or-h2-hint">последние {recent.length} задач, дошедших до финала</span></h2>
        {!recent.length ? (
          <div className="or-empty">пока пусто</div>
        ) : (
          <ul className="or-feed">
            {recent.map(r => (
              <li key={r.send_id} className={`or-feed-item or-feed-${r.status}`}>
                <span className="or-feed-time" title={fmtDateTime(r.ts)}>{fmtListTime(r.ts)}</span>
                <SendStatusBadge status={r.status} />
                {r.session_name ? (
                  <a className="or-feed-session or-link" href={href(sessionPath(r.session_name))} onClick={e => { e.preventDefault(); navigate(sessionPath(r.session_name)) }}>{r.session_name}</a>
                ) : <span className="or-feed-session">—</span>}
                <span className="or-feed-arrow" aria-hidden>→</span>
                {r.session_name && r.recipient && r.recipient.tg_user_id ? (
                  <a className="or-link" href={href(dialogPath(r.session_name, r.recipient.tg_user_id))} onClick={e => { e.preventDefault(); navigate(dialogPath(r.session_name, r.recipient.tg_user_id)) }}>{peerTitle(r.recipient)}</a>
                ) : <span>{peerTitle(r.recipient)}</span>}
                {r.ad ? <>· <AdLink ad={r.ad} /></> : null}
                {r.error ? <span className="or-feed-error" title={r.error}>{r.error}</span> : null}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}

function SessionRow({ session: s, token, href, navigate, now, sharedProxy, onChanged }) {
  const [editing, setEditing] = useState(false)
  const [notice, setNotice] = useState('')
  const open = () => {
    if (!editing) navigate(sessionPath(s.session_name))
  }
  const limit = s.daily_limit == null ? '' : ` / ${s.daily_limit}`
  const p = s.proxy || {}
  const running = sessionRunning(s)

  const saveProxy = async payload => {
    const resp = await apiPost(`/api/outreach/sessions/${encodeURIComponent(s.session_name)}/proxy`, token, payload)
    setNotice(resp && resp.running === false ? 'применится при следующем захвате' : 'воркер переподключится в течение ~20 с')
    setEditing(false)
    onChanged()
  }

  return (
    <tr className="or-row-click" onClick={open}>
      <td>
        <a className="or-session-link" href={href(sessionPath(s.session_name))} onClick={e => { e.preventDefault(); open() }}>
          <Avatar seed={s.session_name} text={s.display_name || s.tg_username || s.session_name} small />
          <span className="or-session-name">
            <span className="or-mono">{s.session_name}</span>
            <span className="or-muted">{accountTitle(s) !== s.session_name ? accountTitle(s) : ''}{s.display_name && s.tg_username ? ` · ${s.display_name}` : ''}</span>
          </span>
        </a>
        {s.has_file === false ? (
          <div><Badge kind="bad" title="строка в БД есть, файла в директории нет">файла нет</Badge></div>
        ) : null}
      </td>
      <td><HealthBadge status={s.status} />{s.note ? <div className="or-muted or-note" title={s.note}>{s.note}</div> : null}</td>
      <td>
        <ActivityDot activity={s.activity} />
        {s.flood_until && s.activity === 'cooldown' ? <div className="or-muted">до {fmtDateTime(s.flood_until)}</div> : null}
      </td>
      <td className="or-mono or-proxy" title={editing ? undefined : proxyHint(p, sharedProxy)}>
        {editing ? (
          <ProxyEditor
            current={ownProxy(p)}
            scope="session"
            placeholder={p.own || (sharedProxy && sharedProxy.value) || PROXY_PLACEHOLDER}
            onSave={saveProxy}
            onCancel={() => setEditing(false)}
          />
        ) : (
          <div className="or-proxy-cell">
            <span>{proxyLabel(p)}</span>
            <button
              type="button"
              className="or-btn-icon"
              title="изменить прокси аккаунта"
              aria-label={`Изменить прокси ${s.session_name}`}
              onClick={e => { e.stopPropagation(); setNotice(''); setEditing(true) }}
            >✎</button>
          </div>
        )}
        <ProxyStatus proxy={p} running={running} notice={notice} />
      </td>
      <td title={s.day_key ? `день ${s.day_key}` : ''}>{s.sent_today == null ? '—' : s.sent_today}{limit}</td>
      <td>{s.sent_last_hour} / {s.sent_last_day}</td>
      <td className="or-current">
        {s.current && s.current.length ? s.current.map(c => (
          <div key={c.send_id} className="or-current-item">
            <SendStatusBadge status={c.status} />
            {c.ad ? <AdLink ad={c.ad} /> : <span className="or-muted">без объявления</span>}
            <span className="or-muted">
              {c.ad && c.ad.ads_count > 1 ? `${c.ad.ads_count} объявл. · ` : ''}
              {peerTitle(c.recipient)} · {fmtAgo(c.claimed_ts, now)}
            </span>
          </div>
        )) : <span className="or-muted">{s.activity === 'running' ? 'ждёт (пауза между отправками / квота / окно)' : '—'}</span>}
        {s.outbox_pending ? (
          <div className="or-current-item">
            <Badge kind="queue" title="ручные сообщения из дашборда, которые ещё не ушли">✉ {s.outbox_pending} в очереди</Badge>
          </div>
        ) : null}
      </td>
      <td>
        {s.last_send ? (
          <div className="or-current-item">
            <span title={fmtDateTime(s.last_send.sent_ts)}>{fmtAgo(s.last_send.sent_ts, now)}</span>
            {s.last_send.ad ? <AdLink ad={s.last_send.ad} /> : null}
            <span className="or-muted">{peerTitle(s.last_send.recipient)}</span>
          </div>
        ) : <span className="or-muted">{s.last_sent_ts ? fmtAgo(s.last_sent_ts, now) : '—'}</span>}
      </td>
      <td>{s.replies_last_day}<span className="or-muted"> / {s.replies_total}</span></td>
      <td className="or-muted" title={fmtDateTime(s.last_seen_ts)}>{s.last_seen_ts ? fmtAgo(s.last_seen_ts, now) : '—'}</td>
    </tr>
  )
}

/* ------------------------------------------------------------------ session */

function SessionHeader({ session, token, onSession }) {
  const [editing, setEditing] = useState(false)
  const [notice, setNotice] = useState('')
  // Общий прокси нужен для подсказки «общий из …» и как placeholder; один запрос,
  // без опроса — настройки меняются только со страницы.
  const [shared, setShared] = useState(null)
  useEffect(() => {
    if (!token) return undefined
    let cancelled = false
    apiGet('/api/outreach/settings', token)
      .then(r => { if (!cancelled && r) setShared(r.proxy || null) })
      .catch(() => { /* подсказка без общего прокси — не страшно */ })
    return () => { cancelled = true }
  }, [token])
  if (!session) return null

  const p = session.proxy || {}
  const running = sessionRunning(session)
  const saveProxy = async payload => {
    const resp = await apiPost(`/api/outreach/sessions/${encodeURIComponent(session.session_name)}/proxy`, token, payload)
    setNotice(resp && resp.running === false ? 'применится при следующем захвате' : 'воркер переподключится в течение ~20 с')
    setEditing(false)
    if (resp && resp.session && onSession) onSession(resp.session)
  }

  return (
    <div className="or-session-header">
      <Avatar seed={session.session_name} text={session.display_name || session.tg_username || session.session_name} />
      <div className="or-session-header-main">
        <div className="or-session-header-title">
          <span className="or-mono">{session.session_name}</span>
          <span className="or-session-header-account">{accountTitle(session) !== session.session_name ? accountTitle(session) : ''}</span>
          {session.display_name && session.tg_username ? <span className="or-muted">{session.display_name}</span> : null}
          {session.phone ? <span className="or-muted">{fmtPhone(session.phone)}</span> : null}
          {session.tg_user_id ? <span className="or-muted">id {session.tg_user_id}</span> : null}
          {session.has_file === false ? <Badge kind="bad" title="строка в БД есть, файла в директории нет">файла нет</Badge> : null}
        </div>
        <div className="or-session-header-meta">
          <HealthBadge status={session.status} />
          <ActivityDot activity={session.activity} />
          <span className="or-mono or-proxy or-proxy-cell" title={proxyHint(p, shared)}>
            прокси: {proxyLabel(p)}
            <button
              type="button"
              className="or-btn-icon"
              title="изменить прокси аккаунта"
              aria-label={`Изменить прокси ${session.session_name}`}
              onClick={() => { setNotice(''); setEditing(e => !e) }}
            >✎</button>
          </span>
          {p.pending === true || proxyNeedsReclaim(p) ? <span className="or-muted">задано: {ownProxyText(p)}</span> : null}
          {p.pending === true ? <Badge kind="warn" title="воркер ещё не применил новый прокси">переподключается…</Badge> : null}
          {!running && proxyNeedsReclaim(p) ? <span className="or-muted">применится при следующем захвате</span> : null}
          {notice ? <span className="or-notice">{notice}</span> : null}
          <span>сегодня {session.sent_today == null ? '—' : session.sent_today}{session.daily_limit == null ? '' : ` / ${session.daily_limit}`}</span>
          <span>отправлено {session.sent_total}</span>
          <span>ошибок {session.failed_total}</span>
          <span>пропущено {session.skipped_total}</span>
          <span>ответов {session.replies_total}</span>
          {session.outbox_pending ? <Badge kind="queue">✉ {session.outbox_pending} ручных в очереди</Badge> : null}
        </div>
        {editing ? (
          <ProxyEditor
            current={ownProxy(p)}
            scope="session"
            placeholder={p.own || (shared && shared.value) || PROXY_PLACEHOLDER}
            onSave={saveProxy}
            onCancel={() => setEditing(false)}
          />
        ) : null}
      </div>
    </div>
  )
}

function DialogList({ token, sessionName, activeTgUserId, href, navigate, now, onSession }) {
  const [q, setQ] = useState('')
  const [limit, setLimit] = useState(DIALOGS_PAGE)
  const query = useDebounced(q.trim(), 300)

  const { data, error, loading, updatedAt, refresh } = usePolling(
    () => apiGet(
      `/api/outreach/sessions/${encodeURIComponent(sessionName)}/dialogs?q=${encodeURIComponent(query)}&limit=${limit}`,
      token
    ),
    DIALOGS_POLL_MS,
    [token, sessionName, query, limit],
    Boolean(token)
  )
  useEffect(() => {
    if (data && data.session && onSession) onSession(data.session)
  }, [data, onSession])
  useEffect(() => { setLimit(DIALOGS_PAGE) }, [query, sessionName])

  const dialogs = (data && data.dialogs) || []

  return (
    <div className="or-dialogs">
      <div className="or-dialogs-search">
        <input
          type="search"
          className="or-input"
          placeholder="Поиск: номер, @username, id"
          value={q}
          onChange={e => setQ(e.target.value)}
          aria-label="Поиск по диалогам"
        />
      </div>
      <StatusLine updatedAt={updatedAt} loading={loading} error={error} onRefresh={refresh} now={now} />
      <ul className="or-dialog-list">
        {!dialogs.length ? (
          <li className="or-empty">{data ? (query ? 'ничего не найдено' : 'диалогов пока нет: сессия ещё ничего не отправляла') : (error ? '' : 'загрузка…')}</li>
        ) : dialogs.map(d => {
          const active = String(d.tg_user_id) === String(activeTgUserId || '')
          const title = peerTitle(d)
          const path = dialogPath(sessionName, d.tg_user_id)
          return (
            <li key={d.tg_user_id} className={`or-dialog${active ? ' or-dialog-active' : ''}`}>
              <a className="or-dialog-link" href={href(path)} onClick={e => { e.preventDefault(); navigate(path) }}>
                <Avatar seed={d.tg_user_id} text={d.tg_username || d.phone || String(d.tg_user_id)} />
                <span className="or-dialog-body">
                  <span className="or-dialog-top">
                    <span className="or-dialog-title">{title}</span>
                    <span className="or-dialog-time" title={fmtDateTime(d.last_ts)}>{fmtListTime(d.last_ts)}</span>
                  </span>
                  <span className="or-dialog-bottom">
                    <span className="or-dialog-preview">
                      {d.last_direction === 'out' ? <span className="or-dialog-you">Вы: </span> : null}
                      {d.last_text || <span className="or-muted">(без текста)</span>}
                    </span>
                    <span className="or-dialog-badges">
                      {d.has_stop || d.do_not_contact ? <Badge kind="bad" title={d.do_not_contact_reason || 'стоп-слово'}>СТОП</Badge> : null}
                      {d.in_count ? <span className="or-counter or-counter-in" title="входящих">{d.in_count}</span> : null}
                      <span className="or-counter" title="отправлено нами">{d.out_count}</span>
                    </span>
                  </span>
                  {d.tg_username && d.phone ? <span className="or-dialog-sub">{fmtPhone(d.phone)}</span> : null}
                </span>
              </a>
            </li>
          )
        })}
      </ul>
      {data && data.has_more ? (
        <button type="button" className="or-btn or-btn-more" onClick={() => setLimit(l => l + DIALOGS_PAGE)}>Показать ещё</button>
      ) : null}
    </div>
  )
}

/* ------------------------------------------------------------------ messages */

function SystemRow({ message }) {
  const m = message
  const label = SEND_STATUS_LABEL[m.status] || m.status
  const kind = m.status === 'failed' ? 'bad' : (m.status === 'skipped' ? 'neutral' : 'live')
  return (
    <div className="or-msg-system" title={fmtDateTime(m.ts)}>
      <Badge kind={kind}>{label}</Badge>
      <span className="or-msg-system-text">
        {m.campaign ? <span className="or-mono">{m.campaign}</span> : null}
        {m.attempt ? ` · попытка ${m.attempt}` : ''}
        {m.error ? ` · ${m.error}` : ''}
        {' · '}{fmtClock(m.ts)}
      </span>
    </div>
  )
}

function Bubble({ message, onCancel, onRetry, busy }) {
  const m = message
  const out = m.direction === 'out'
  const manual = m.kind === 'manual'
  const pending = isPendingManual(m)
  const failed = manual && m.status === 'failed'
  const cancelled = manual && m.status === 'cancelled'
  const cls = [
    'or-msg',
    out ? 'or-msg-out' : 'or-msg-in',
    pending ? 'or-msg-pending' : '',
    failed ? 'or-msg-failed' : '',
    cancelled ? 'or-msg-cancelled' : '',
  ].filter(Boolean).join(' ')
  return (
    <div className={cls}>
      <div className="or-bubble">
        {manual ? <div className="or-bubble-kind">вручную из дашборда</div> : null}
        {out && m.ad ? (
          <div className="or-bubble-ad">
            <span className="or-muted">объявление:</span> <AdLink ad={m.ad} />
            {m.ad.ads_count > 1 ? <span className="or-muted"> · всего {m.ad.ads_count}</span> : null}
            {m.campaign ? <span className="or-muted"> · {m.campaign}</span> : null}
          </div>
        ) : null}
        <div className="or-bubble-text">{m.text || <span className="or-muted">(без текста)</span>}</div>
        {failed && m.error ? <div className="or-bubble-error">{m.error}</div> : null}
        <div className="or-bubble-meta" title={fmtDateTime(m.ts)}>
          {!out && m.is_stop ? <Badge kind="bad">стоп-слово</Badge> : null}
          {pending ? <Badge kind="queue">{MANUAL_STATUS_LABEL[m.status]}</Badge> : null}
          {failed ? <Badge kind="bad">{MANUAL_STATUS_LABEL.failed}</Badge> : null}
          {cancelled ? <Badge kind="neutral">{MANUAL_STATUS_LABEL.cancelled}</Badge> : null}
          {manual && m.status === 'queued' && onCancel ? (
            <button type="button" className="or-btn-link" disabled={busy} onClick={() => onCancel(m)}>Отменить</button>
          ) : null}
          {failed && onRetry ? (
            <button type="button" className="or-btn-link" disabled={busy} onClick={() => onRetry(m)}>Повторить</button>
          ) : null}
          {m.tg_message_id ? <span className="or-muted">#{m.tg_message_id}</span> : null}
          <span>{fmtClock(m.ts)}</span>
          {out && !manual ? <span className="or-check" aria-label="отправлено">✓</span> : null}
          {manual && m.status === 'sent' ? <span className="or-check" aria-label="отправлено">✓</span> : null}
        </div>
      </div>
    </div>
  )
}

/* Поле ввода как в мессенджере: Enter — отправить, Shift+Enter — перенос строки. */
function Composer({ disabledReason, warning, notice, error, busy, onSend }) {
  const [draft, setDraft] = useState('')
  const rows = Math.min(6, Math.max(1, draft.split('\n').length))
  const tooLong = draft.length > MESSAGE_MAX_CHARS
  const canSend = !disabledReason && !busy && draft.trim().length > 0 && !tooLong

  const submit = async () => {
    if (!canSend) return
    const ok = await onSend(draft)
    if (ok) setDraft('')
  }
  const onKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      submit()
    }
  }

  return (
    <form className="or-composer" onSubmit={e => { e.preventDefault(); submit() }}>
      <div className="or-composer-row">
        <textarea
          className="or-input or-composer-input"
          rows={rows}
          value={draft}
          disabled={Boolean(disabledReason) || busy}
          placeholder={disabledReason || 'Написать от имени этого аккаунта… (Enter — отправить, Shift+Enter — новая строка)'}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          aria-label="Текст сообщения"
        />
        <button type="submit" className="or-btn or-btn-send" disabled={!canSend}>
          {busy ? 'Отправка…' : 'Отправить'}
        </button>
      </div>
      {draft.length > MESSAGE_MAX_CHARS - 500 ? (
        <div className={`or-composer-note${tooLong ? ' or-composer-error' : ''}`}>{draft.length} / {MESSAGE_MAX_CHARS}</div>
      ) : null}
      {error ? <div className="or-composer-note or-composer-error">{error}</div> : null}
      {!error && warning ? <div className="or-composer-note or-composer-warn">{warning}</div> : null}
      {!error && !warning && notice ? <div className="or-composer-note">{notice}</div> : null}
      {!error && !warning && !notice && !disabledReason ? (
        <div className="or-composer-note">Сообщение встанет в очередь воркера этой сессии и уйдёт через несколько секунд; статус виден в чате.</div>
      ) : null}
    </form>
  )
}

function MessagesPane({ token, sessionName, tgUserId, href, navigate, now, onSession }) {
  const [pollMs, setPollMs] = useState(MESSAGES_POLL_MS)
  const { data, error, loading, updatedAt, refresh } = usePolling(
    () => apiGet(
      `/api/outreach/sessions/${encodeURIComponent(sessionName)}/dialogs/${encodeURIComponent(tgUserId)}/messages?limit=${MESSAGES_LIMIT}`,
      token
    ),
    pollMs,
    [token, sessionName, tgUserId],
    Boolean(token)
  )
  useEffect(() => {
    if (data && data.session && onSession) onSession(data.session)
  }, [data, onSession])

  const listRef = useRef(null)
  const stickToBottomRef = useRef(true)
  const messages = (data && data.messages) || []
  const hasPending = messages.some(isPendingManual)
  useEffect(() => {
    setPollMs(hasPending ? MESSAGES_POLL_FAST_MS : MESSAGES_POLL_MS)
  }, [hasPending])
  const lastKey = messages.length ? `${messages[messages.length - 1].kind}-${messages[messages.length - 1].id}-${messages[messages.length - 1].status}` : ''

  // Отправка: POST кладёт сообщение в outreach.outbox, дальше его судьбу
  // показывает опрос (queued -> sending -> sent/failed).
  const [busy, setBusy] = useState(false)
  const [composerError, setComposerError] = useState('')
  const [notice, setNotice] = useState('')
  const session = data && data.session
  const peer = data && data.peer
  const sendText = async text => {
    const body = String(text || '').trim()
    if (!body || busy) return false
    if (peer && peer.do_not_contact) {
      const ok = window.confirm('Этот человек отписался (ответил стоп-словом). Всё равно отправить?')
      if (!ok) return false
    }
    setBusy(true)
    setComposerError('')
    try {
      const resp = await apiPost(
        `/api/outreach/sessions/${encodeURIComponent(sessionName)}/dialogs/${encodeURIComponent(tgUserId)}/messages`,
        token,
        { text: body }
      )
      setNotice(resp && resp.worker_running === false
        ? 'Воркер этой сессии сейчас не запущен: сообщение в очереди и уйдёт, когда он поднимется (outreach.sh start) или кончится карантин.'
        : '')
      stickToBottomRef.current = true
      refresh()
      return true
    } catch (e) {
      setComposerError(describeError(e))
      return false
    } finally {
      setBusy(false)
    }
  }
  const cancelMessage = async m => {
    setBusy(true)
    setComposerError('')
    try {
      await apiPost(`/api/outreach/outbox/${encodeURIComponent(m.id)}/cancel`, token)
      refresh()
    } catch (e) {
      setComposerError(describeError(e))
    } finally {
      setBusy(false)
    }
  }
  const retryMessage = m => sendText(m.text)

  let disabledReason = ''
  if (data && data.outbox_enabled === false) {
    disabledReason = 'Отправка недоступна: tg-outreach не обновлён (нет таблицы outreach.outbox)'
  } else if (session && session.activity === 'dead') {
    disabledReason = `Аккаунт ${HEALTH_LABEL[session.status] || session.status} — отправка невозможна`
  }
  let warning = ''
  if (session && session.activity === 'cooldown') {
    warning = `Аккаунт в карантине${session.flood_until ? ` до ${fmtDateTime(session.flood_until)}` : ''}: сообщение встанет в очередь и уйдёт после.`
  } else if (session && !session.running && session.activity !== 'dead') {
    warning = 'Сессию сейчас не держит ни один воркер: сообщение подождёт в очереди до outreach.sh start.'
  } else if (peer && peer.do_not_contact) {
    warning = 'Собеседник отписался (стоп-слово). Перед отправкой будет подтверждение.'
  }

  // Как в чате: при открытии и при новых сообщениях прокручиваем вниз, если
  // пользователь и так был внизу; историю, которую он читает, не дёргаем.
  useEffect(() => {
    const el = listRef.current
    if (el && stickToBottomRef.current) el.scrollTop = el.scrollHeight
  }, [lastKey, tgUserId])
  const onScroll = () => {
    const el = listRef.current
    if (!el) return
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40
  }

  const rows = []
  let lastDay = ''
  messages.forEach(m => {
    const day = fmtDay(m.ts)
    if (day && day !== lastDay) {
      rows.push(<div key={`day-${day}-${m.id}`} className="or-day"><span>{day}</span></div>)
      lastDay = day
    }
    const key = `${m.kind || m.direction}-${m.id}`
    if (m.kind === 'campaign' && m.status !== 'sent') {
      // Рассылочная задача, не дошедшая до отправки, — системной строкой.
      rows.push(<SystemRow key={key} message={m} />)
    } else {
      // Ручное сообщение — всегда пузырём, в любом статусе: админ его набрал
      // и должен видеть его судьбу там же, где набирал.
      rows.push(<Bubble key={key} message={m} onCancel={cancelMessage} onRetry={retryMessage} busy={busy} />)
    }
  })

  return (
    <div className="or-chat">
      <div className="or-chat-header">
        <a
          className="or-chat-back or-link"
          href={href(sessionPath(sessionName))}
          onClick={e => { e.preventDefault(); navigate(sessionPath(sessionName)) }}
          aria-label="К списку диалогов"
        >←</a>
        <Avatar seed={tgUserId} text={(peer && (peer.tg_username || peer.phone)) || String(tgUserId)} />
        <div className="or-chat-peer">
          <div className="or-chat-peer-title">
            {peerTitle(peer || { tg_user_id: tgUserId })}
            {peer && peer.do_not_contact ? <Badge kind="bad" title={peer.do_not_contact_reason || ''}>отписался</Badge> : null}
          </div>
          <div className="or-chat-peer-sub">
            {peerSubtitle(peer || { tg_user_id: tgUserId })}
            {peer && peer.resolve_status ? ` · ${peer.resolve_status}` : ''}
          </div>
        </div>
        <StatusLine updatedAt={updatedAt} loading={loading} error={error} onRefresh={refresh} now={now} />
      </div>
      <div className="or-chat-scroll" ref={listRef} onScroll={onScroll}>
        {data && data.truncated ? <div className="or-day"><span>показаны последние {MESSAGES_LIMIT}</span></div> : null}
        {!messages.length ? (
          <div className="or-empty">{data ? 'сообщений нет' : (error ? '' : 'загрузка…')}</div>
        ) : rows}
      </div>
      <Composer
        disabledReason={disabledReason}
        warning={warning}
        notice={notice}
        error={composerError}
        busy={busy}
        onSend={sendText}
      />
    </div>
  )
}

function SessionView({ token, route, href, navigate, now }) {
  const [session, setSession] = useState(null)
  const onSession = useCallback(s => setSession(s), [])
  useEffect(() => { setSession(null) }, [route.session])
  const hasDialog = route.view === 'dialog'

  return (
    <div className="or-session">
      <SessionHeader session={session} token={token} onSession={onSession} />
      <div className={`or-split${hasDialog ? ' or-split-has-dialog' : ''}`}>
        <aside className="or-split-left">
          <DialogList
            token={token}
            sessionName={route.session}
            activeTgUserId={route.tgUserId}
            href={href}
            navigate={navigate}
            now={now}
            onSession={onSession}
          />
        </aside>
        <main className="or-split-right">
          {hasDialog ? (
            <MessagesPane
              key={`${route.session}/${route.tgUserId}`}
              token={token}
              sessionName={route.session}
              tgUserId={route.tgUserId}
              href={href}
              navigate={navigate}
              now={now}
              onSession={onSession}
            />
          ) : (
            <div className="or-chat-placeholder">Выберите диалог слева — переписка откроется здесь, как в Telegram</div>
          )}
        </main>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ page */

/* Состояние входа: checking — токен из localStorage проверяется GET /api/admin/me;
   ready — админ вошёл; anon — токена нет или он протух; error — /me не ответил
   (сеть/5xx), токен оставляем и предлагаем повторить. */
function initialAuth() {
  const token = loadAdminToken()
  return { phase: token ? 'checking' : 'anon', token, login: '', error: null, hint: '' }
}

export default function OutreachPage() {
  const [auth, setAuth] = useState(initialAuth)
  // Экран уже показывали с валидной сессией — при протухании токена форма входа
  // ложится поверх него, не разрушая состояние (выбранные файлы, черновики).
  const [sessionSeen, setSessionSeen] = useState(false)
  const [route, setRoute] = useState(() => parseRoute(typeof window !== 'undefined' ? window.location.pathname : '/outreach'))
  const now = useNow(1000)

  useEffect(() => {
    stripLegacyDebugParam()
    const onPop = () => setRoute(parseRoute(window.location.pathname))
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  // Любой 401 (опрос, загрузка, прокси) — токен сбрасываем, форма входа поверх.
  // 401 от запроса со старым токеном (ушёл до повторного входа) — игнорируем.
  const tokenRef = useRef(auth.token)
  tokenRef.current = auth.token
  useEffect(() => {
    setUnauthorizedHandler(err => {
      if (err && err.token && err.token !== tokenRef.current) return
      saveAdminToken('')
      const detail = errDetail(err)
      const hint = /not configured|is disabled/i.test(detail) ? detail : ''
      setAuth(prev => (prev.phase === 'anon' ? { ...prev, hint: hint || prev.hint } : { phase: 'anon', token: '', login: '', error: null, hint }))
    })
    return () => setUnauthorizedHandler(null)
  }, [])

  // Проверка сохранённого токена до первого опроса.
  const checking = auth.phase === 'checking'
  const token = auth.token
  useEffect(() => {
    if (!checking || !token) return undefined
    let cancelled = false
    apiGet('/api/admin/me', token, { skipAuthHook: true })
      .then(me => {
        if (cancelled) return
        setAuth({ phase: 'ready', token, login: (me && me.login) || '', error: null, hint: '' })
        setSessionSeen(true)
      })
      .catch(err => {
        if (cancelled) return
        if (err.status === 401) {
          saveAdminToken('')
          setAuth({ phase: 'anon', token: '', login: '', error: null, hint: /not configured|is disabled/i.test(errDetail(err)) ? errDetail(err) : '' })
        } else {
          setAuth(prev => ({ ...prev, phase: 'error', error: err }))
        }
      })
    return () => { cancelled = true }
  }, [checking, token])

  useEffect(() => {
    const parts = ['Рассылка']
    if (route.session) parts.unshift(route.session)
    if (route.tgUserId) parts.unshift(`диалог ${route.tgUserId}`)
    document.title = `${parts.join(' · ')} — Realty Map`
    return () => { document.title = 'Realty Map' }
  }, [route])

  const onLogin = useCallback(resp => {
    saveAdminToken(resp.token)
    tokenRef.current = resp.token
    setAuth({ phase: 'ready', token: resp.token, login: resp.login || '', error: null, hint: '' })
    setSessionSeen(true)
  }, [])

  const logout = useCallback(() => {
    const current = auth.token
    if (current) apiPost('/api/admin/logout', current, undefined, { skipAuthHook: true }).catch(() => { /* токен всё равно забываем */ })
    saveAdminToken('')
    tokenRef.current = ''
    setAuth({ phase: 'anon', token: '', login: '', error: null, hint: '' })
    setSessionSeen(false)
  }, [auth.token])

  const retryCheck = useCallback(() => {
    setAuth(prev => ({ ...prev, phase: 'checking', error: null }))
  }, [])

  // Адреса без секрета: путь и есть ссылка.
  const href = useCallback(path => path, [])
  const navigate = useCallback(path => {
    try {
      window.history.pushState({}, '', href(path))
    } catch (_) { /* pushState затроттлен — состояние всё равно обновим */ }
    setRoute(parseRoute(path))
    window.scrollTo(0, 0)
  }, [href])

  const crumbs = [{ label: 'Рассылка', path: '/outreach' }]
  if (route.session) crumbs.push({ label: route.session, path: sessionPath(route.session) })
  if (route.tgUserId) crumbs.push({ label: `диалог ${route.tgUserId}`, path: dialogPath(route.session, route.tgUserId) })

  const overlay = auth.phase === 'anon' && sessionSeen
  let body
  if (auth.phase === 'checking') {
    body = <div className="or-gate-placeholder" role="status">проверяю сессию…</div>
  } else if (auth.phase === 'error') {
    body = (
      <div className="or-gate">
        <div className="or-gate-card">
          <h2 className="or-gate-title">Не удалось проверить сессию</h2>
          <div className="or-form-error" role="alert">{describeError(auth.error)}</div>
          <div className="or-gate-actions">
            <button type="button" className="or-btn or-btn-primary" onClick={retryCheck}>Повторить</button>
            <button type="button" className="or-btn" onClick={logout}>Выйти</button>
          </div>
        </div>
      </div>
    )
  } else if (auth.phase === 'anon' && !overlay) {
    body = <AdminGate hint={auth.hint} onLogin={onLogin} />
  } else {
    body = (
      <>
        <div className="or-body" inert={overlay ? '' : undefined}>
          {route.view === 'overview' ? (
            <Overview token={auth.token} href={href} navigate={navigate} now={now} />
          ) : (
            <SessionView token={auth.token} route={route} href={href} navigate={navigate} now={now} />
          )}
        </div>
        {overlay ? (
          <div className="or-gate-overlay">
            <AdminGate overlay hint={auth.hint} onLogin={onLogin} />
          </div>
        ) : null}
      </>
    )
  }

  return (
    <div className="or-page">
      <header className="or-header">
        <a className="or-back" href="/">← Карта</a>
        <nav className="or-crumbs" aria-label="Навигация">
          {crumbs.map((c, i) => (
            <React.Fragment key={c.path}>
              {i ? <span className="or-crumb-sep" aria-hidden>/</span> : null}
              {i === crumbs.length - 1 ? (
                <span className="or-crumb or-crumb-current">{c.label}</span>
              ) : (
                <a className="or-crumb or-link" href={href(c.path)} onClick={e => { e.preventDefault(); navigate(c.path) }}>{c.label}</a>
              )}
            </React.Fragment>
          ))}
        </nav>
        {auth.phase === 'ready' ? (
          <div className="or-header-admin">
            <span className="or-header-login" title="вход администратора">admin · {auth.login || '?'}</span>
            <button type="button" className="or-btn or-btn-small" onClick={logout}>Выйти</button>
          </div>
        ) : null}
      </header>
      {body}
    </div>
  )
}
